import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EmployeeDashboard } from '../../types/employee-dashboard';
import { dashboardRowPhase, normalizeMyPhase } from '../../lib/job-phase-dates';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/Header';
import SignOutButton from '../../components/SignOutButton';
import FieldClockAction from '../../components/field/FieldClockAction';
import FieldTileGrid from '../../components/field/FieldTileGrid';
import { api } from '../../api/client';
import { crewRoleLabel } from '../../lib/display-labels';
import { jobStatusLabel, jobStatusStyle } from '../../lib/job-status';
import {
  Layers,
  Gauge,
  ClipboardList,
  ClipboardCheck,
  Clock,
  Ticket,
  ChevronRight,
  MapPin,
  Wrench,
  CalendarOff,
  ShieldCheck,
  Target,
  Handshake,
  Brain,
  HardHat,
  TrendingUp,
  Users,
  Award,
  Shield,
  Bell,
  DollarSign,
  UserCircle,
  CalendarDays,
  History,
  ReceiptText
} from 'lucide-react';
import { formatCalendarDate, tenantHour } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useMyWork } from '../../hooks/useMyWork';

/** Parse a YYYY-MM-DD date string as local time (not UTC). @see formatCalendarDate. */
const parseLocalDate = (dateStr: string | null | undefined): string =>
  formatCalendarDate(dateStr, { month: 'short', day: 'numeric' }) ?? '';

// ─── Core Values (using SVG icons instead of emojis for native compatibility) ─
/*
 * These are Smith Casing's own values, written in its voice ("When Smith shows
 * up…", "Casing is our trade"). They were shown once per session to every
 * field tenant — live casing customers and PT and downhole techs included.
 * Operator decision 2026-10-08: Smith Casing only, everyone else never. Match
 * on the company id, not the name.
 */
export const CORE_VALUES_COMPANY_ID = '8dd676f5-6dbb-4020-bb9d-d7b7f2790214';

const CORE_VALUES = [
  { icon: ShieldCheck, title: 'Safety First. Always.', body: 'Every job starts and ends with safety. We protect our people, our clients, and the wellsite by doing the job right \u2014 no shortcuts, no compromises.' },
  { icon: Target, title: 'Precision & Execution', body: 'Casing is unforgiving. We bring discipline, focus, and technical excellence to every connection, every run, every time.' },
  { icon: Handshake, title: 'Trust Through Reliability', body: 'When Smith shows up, the job gets done. Our clients count on consistent crews, dependable equipment, and results they don\u2019t have to worry about.' },
  { icon: Brain, title: 'Experience-Driven Decisions', body: 'We respect procedure, but we rely on field-tested judgment. Our crews think ahead, adapt fast, and solve problems before they become downtime.' },
  { icon: HardHat, title: 'Pride in Craftsmanship', body: 'Casing is our trade \u2014 and we take pride in it. Clean work, professional crews, and a reputation built run by run.' },
  { icon: TrendingUp, title: 'Continuous Improvement', body: 'We\u2019re never satisfied with \u201Cgood enough.\u201D We invest in training, technology, and better processes to raise the standard on every job.' },
  { icon: Users, title: 'Team Before Ego', body: 'Strong crews beat lone heroes. We work together, watch each other\u2019s backs, and win as a team \u2014 on site and off.' },
];

function CoreValuesOverlay({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const val = CORE_VALUES[step];
  const isLast = step === CORE_VALUES.length - 1;

  return (
    <div className="fixed inset-0 bg-fieldiq-dark flex flex-col" style={{ zIndex: 60 }}>
      {/* Title */}
      <div className="px-4 pt-6 pb-1 safe-top">
        <h1 className="text-center text-lg font-bold text-white tracking-wide">Core Values</h1>
      </div>

      {/* Progress dots */}
      <div className="px-4 pb-2 flex justify-center gap-1.5">
        {CORE_VALUES.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i === step ? 'w-6 bg-fieldiq-accent' : i < step ? 'w-3 bg-fieldiq-accent/50' : 'w-3 bg-fieldiq-border'
            }`}
          />
        ))}
      </div>

      {/* Value card */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center max-w-sm animate-fade-in" key={step}>
          <div className="mb-6 flex justify-center">
            <val.icon size={56} className="text-fieldiq-accent" />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">{val.title}</h2>
          <p className="text-fieldiq-text leading-relaxed">{val.body}</p>
        </div>
      </div>

      {/* Bottom actions */}
      <div className="px-6 pb-12 space-y-3 safe-bottom">
        <button
          onClick={() => isLast ? onDone() : setStep(s => s + 1)}
          className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3.5 rounded-xl text-sm hover:bg-amber-400 transition-colors"
        >
          {isLast ? 'Let\u2019s Go' : 'Next'}
        </button>
        <p className="text-center text-[11px] text-fieldiq-muted">{step + 1} of {CORE_VALUES.length}</p>
      </div>
    </div>
  );
}

// ─── Home shortcuts ─────────────────────────────────────────────
//
// THIS IS NOW THE WHOLE OF THE NAVIGATION for a casing field worker. The
// bottom tab bar is gone (see the shell comment in App.tsx), so a destination
// missing from here is a destination with no way in, not merely a slower one.
//
// FieldMoreScreen is still the source of truth for the list and its route is
// still registered -- an entry added there wants a matching entry here. Two
// tiles have no More entry: My Jobs, and Safety, which was a tab rather than a
// menu row and would otherwise have been lost with the bar.
//
// Sign Out is still not a tile -- a destructive action does not belong among
// navigation targets -- but it is no longer absent from this screen. It sits
// below the tiles, on its own, because More is no longer somewhere a hand
// passes through.
//
// Subtitles are shorter than the More screen's because a half-width tile at
// 390px fits roughly 27 characters; the longer sentences there wrap to three
// lines and the rows stop lining up.
//
// THREE TILES ARE CASING'S OWN, and carry `vertical` (PT-3). My Jobs, My Tickets
// and My Inspections read casing endpoints (/employee/my-jobs, /employee/my-tickets,
// /casing/inspection-submissions). A pressure-testing hand lands in this tree too,
// and for them those tiles opened "No jobs found" over an active PT job and a raw
// 403 sentence. A POSITIVE guard on casing_running: a casing tenant renders exactly
// the tiles it did before, and a tenant without casing does not get doors into a
// vertical it does not run.
export const SHORTCUTS: { title: string; subtitle: string; icon: typeof Wrench; path: string; color: string; vertical?: string }[] = [
  { title: 'My Jobs', subtitle: 'Scheduled & active', icon: HardHat, path: '/field/jobs', color: 'text-fieldiq-accent', vertical: 'casing_running' },
  { title: 'My Tickets', subtitle: 'Your field tickets', icon: Ticket, path: '/field/tickets', color: 'text-amber-400', vertical: 'casing_running' },
  // Third, not last. Safety was one of the five tabs on the removed bar and is
  // the parent of Forms and My Inspections below it; burying it under the pay
  // and profile rows would make it the one destination the bar surfaced better.
  { title: 'Safety', subtitle: 'Meetings, JSAs & incidents', icon: Shield, path: '/field/safety', color: 'text-red-400' },
  { title: 'Forms', subtitle: 'Safety & inspection forms', icon: ClipboardList, path: '/field/forms', color: 'text-sky-400' },
  { title: 'My Inspections', subtitle: 'Filed & unfinished', icon: History, path: '/field/safety/inspections', color: 'text-sky-400', vertical: 'casing_running' },
  { title: 'Equipment', subtitle: 'Assigned gear & checks', icon: Wrench, path: '/field/equipment', color: 'text-fieldiq-accent' },
  { title: 'Certifications', subtitle: 'Your certs & expiry dates', icon: Award, path: '/field/certs', color: 'text-green-400' },
  { title: 'Time Off', subtitle: 'Request & view', icon: CalendarDays, path: '/field/time-off', color: 'text-cyan-400' },
  { title: 'Pay', subtitle: 'Periods & earnings', icon: DollarSign, path: '/field/pay', color: 'text-amber-400' },
  { title: 'Expenses', subtitle: 'Receipts & claims', icon: ReceiptText, path: '/field/expenses', color: 'text-emerald-400' },
  { title: 'Notifications', subtitle: 'How you get notified', icon: Bell, path: '/field/settings/notifications', color: 'text-purple-400' },
  { title: 'Profile', subtitle: 'Your info & password', icon: UserCircle, path: '/field/profile', color: 'text-blue-400' },
];

/**
 * Destinations that belong to ONE vertical, shown above the stats only when
 * the tenant has it. A positive guard per entry: a casing-only tenant renders
 * none of these, and a tenant with two verticals gets both doors. Kept out of
 * SHORTCUTS so the tile grid a casing hand sees is unchanged. FieldMoreScreen
 * lists the same entries under the same guard; the shortcuts test holds the
 * two lists together.
 */
export const VERTICAL_ENTRIES: { vertical: string; title: string; subtitle: string; icon: typeof Wrench; path: string }[] = [
  // A downhole_tools tenant's field hands land on THIS screen (App.tsx: not
  // trucking, not rentals), so this is their way in to /field/downhole.
  { vertical: 'downhole_tools', title: 'Downhole tools', subtitle: 'Assemblies, runs, teardown and your work orders', icon: Layers, path: '/field/downhole' },
  // A pressure_testing tenant's field hands land here too: their way in to /field/pt (PT-3).
  { vertical: 'pressure_testing', title: 'My PT Jobs', subtitle: 'Your test jobs, holds and chart photos', icon: Gauge, path: '/field/pt' },
  // An ndt_inspection tenant's field hands land here too: their way in to /field/ndt (fieldiq-api#1144).
  { vertical: 'ndt_inspection', title: 'My NDT Jobs', subtitle: 'Your crewed jobs, welds and exams', icon: ClipboardCheck, path: '/field/ndt' },
  // A pipeline_facility tenant's field hands land here too: their way in to /field/pf (fieldiq-api#1200).
  { vertical: 'pipeline_facility', title: 'My PF Jobs', subtitle: 'Your crewed jobs and daily logs', icon: ClipboardList, path: '/field/pf' },
  // PF-3a (fieldiq-api#1219): the digs on those jobs, and the dig sheet.
  { vertical: 'pipeline_facility', title: 'My PF Digs', subtitle: 'Features found in the ditch, and the dig steps', icon: Layers, path: '/field/pf/digs' },
  // A well_testing tenant's field hands land here too: their way in to /field/wt (fieldiq-api#1245, WT-2).
  { vertical: 'well_testing', title: 'My WT Jobs', subtitle: 'Your crewed jobs and day / night shift logs', icon: ClipboardList, path: '/field/wt' },
  // A well_servicing hand's way in to /field/ws: the rig's daily tour sheets (fieldiq-api#1282, WS-2).
  { vertical: 'well_servicing', title: 'My Tours', subtitle: 'Your rig jobs and daily tour sheets', icon: ClipboardList, path: '/field/ws' },
  // The shared BOP register (BOP-1/BOP-2), read only for a hand: certificates, the stack, tests, drills, gauges.
  { vertical: 'well_servicing', title: 'Pressure Control', subtitle: 'BOP certificates, stack, tests and drills', icon: Shield, path: '/equipment/pressure-control' },
  // An earthworks tenant's field hands land here too: their way in to /field/ew (fieldiq-api#1284, EW-4).
  { vertical: 'earthworks', title: 'My EW Jobs', subtitle: 'Your crewed jobs, daily logs, loads and dump tickets', icon: ClipboardList, path: '/field/ew' },
  // A wireline_slickline hand's way in to /field/wl: the unit's day sheets and runs (fieldiq-api#1338, WL-2).
  { vertical: 'wireline_slickline', title: 'My Sheets', subtitle: 'Your unit jobs, day sheets and runs', icon: ClipboardList, path: '/field/wl' },
];

// ─── Main Screen ────────────────────────────────────────────────────────────
/**
 * Total / Active / Upcoming for the hand's pressure-testing jobs, from the
 * `summary` row the API computes (api#972). Not a count of `items`: the
 * summary covers every assigned job, and `items` is cut to the open scope.
 */
function PtStatsStrip() {
  const navigate = useNavigate();
  const { data, isError } = useMyWork('open', { enabled: true });
  const row = data?.summary?.find((s) => s.vertical === 'pressure_testing');
  const show = (n: number | undefined) => (typeof n === 'number' ? n : '—');
  return (
    <div className="mb-6" data-testid="field-home-pt-stats">
      <div className="grid grid-cols-3 gap-3">
        <button onClick={() => navigate('/field/pt')} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
          <ClipboardList size={20} className="mx-auto mb-1 text-fieldiq-accent" />
          <div className="text-2xl font-bold text-fieldiq-accent">{show(row?.total)}</div>
          <div className="text-xs text-fieldiq-text mt-1">PT Jobs</div>
        </button>
        <button onClick={() => navigate('/field/pt')} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
          <Clock size={20} className="mx-auto mb-1 text-green-400" />
          <div className="text-2xl font-bold text-green-400">{show(row?.active)}</div>
          <div className="text-xs text-fieldiq-text mt-1">Active</div>
        </button>
        <button onClick={() => navigate('/field/pt')} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
          <ClipboardList size={20} className="mx-auto mb-1 text-amber-400" />
          <div className="text-2xl font-bold text-amber-400">{show(row?.upcoming)}</div>
          <div className="text-xs text-fieldiq-text mt-1">Upcoming</div>
        </button>
      </div>
      {isError && (
        <p className="mt-2 text-xs text-fieldiq-muted">Couldn't load your pressure-testing jobs. Try again shortly.</p>
      )}
    </div>
  );
}

export default function FieldHomeScreen() {
  const navigate = useNavigate();
  const { user, company, hasVertical } = useAuth();

  // Show core values once per session — Smith Casing only (see CORE_VALUES_COMPANY_ID).
  const [showValues, setShowValues] = useState(false);
  const showsCoreValues = company?.id === CORE_VALUES_COMPANY_ID;

  useEffect(() => {
    if (!showsCoreValues) return;
    const key = 'fieldiq_values_seen';
    if (!sessionStorage.getItem(key)) {
      setShowValues(true);
      sessionStorage.setItem(key, '1');
    }
  }, [showsCoreValues]);

  const { data, isLoading } = useQuery({
    queryKey: ['field-dashboard'],
    queryFn: (): Promise<EmployeeDashboard> => api.employee.dashboard().then(r => r.data.data ?? r.data),
  });

  // The tenant's clock, like every other time on this screen — not the phone's.
  const timeZone = useTenantTimeZone();
  const hour = tenantHour(timeZone);
  const greeting = hour === null ? 'Hello' : hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // ─── Which of the job's TWO dated days is this row about? ─────────────────
  //
  // A casing job has a thread wash and a run, often days apart, and the crew
  // works both. API #432 made the dashboard surface a job on EITHER day and
  // stamped each row with `day_phase`. Before that a hand only ever saw the run
  // date, so the label was unnecessary; now the row is ambiguous without it and
  // an ambiguous row is worse than an absent one -- it sends someone to site
  // for the wrong work.
  //
  // 'both' is its own label rather than falling back to 'run'. Wash and run on
  // one day is a longer day, not a normal run, and the hand should see that
  // before they leave.
  const PHASE_LABEL: Record<'wash' | 'run' | 'both', string> = {
    wash: 'Thread Wash',
    run: 'Casing Run',
    both: 'Wash + Run',
  };

  // Amber is the accent this app already uses for "the thing to notice". Run
  // keeps the neutral card treatment because it is the ordinary case; giving
  // every row a coloured chip would make none of them mean anything.
  const PHASE_STYLE: Record<'wash' | 'run' | 'both', string> = {
    wash: 'bg-fieldiq-accent/20 text-fieldiq-accent',
    both: 'bg-fieldiq-accent/20 text-fieldiq-accent',
    run: 'bg-fieldiq-border/40 text-fieldiq-text',
  };

  /**
   * The OTHER phase's day, when there is one worth naming.
   *
   * Both days are read with formatCalendarDate, and that is deliberate.
   * `scheduled_date` and `date_time_wash` are the job WALL-CLOCK columns (see
   * "THE JOB WALL-CLOCK COLUMNS" in lib/calendar-date.ts): the office's typed
   * date and time, stamped UTC verbatim. So the day in the stored value IS the
   * day the office typed, and it is read as written, never converted.
   *
   * This line used to read the wash as an instant in the DEVICE's zone (fe#463,
   * kept through fe#832; the API side of that asymmetry was api#432 / api#731,
   * removed in api#846). That put a wash typed 06:00 on the previous day on any
   * phone west of Greenwich -- Washed Nov 15 for a wash typed for Nov 16 --
   * while the run day, one line up, read correctly. Same kind of column, same
   * helper.
   *
   * Returns null on a 'both' row: there is no other day, and on a same-day job
   * naming one would read as a second trip.
   *
   * Returns null for a single-phase hand (`my_phase` 'wash' or 'run'), too: a
   * wash-only hand sees only the wash and its date (TNT J-4404) — "Run Nov 9"
   * under their wash row named a day they do not work.
   */
  const otherPhase = (job: any): string | null => {
    const mine = normalizeMyPhase(job.my_phase);
    if (mine === 'wash' || mine === 'run') return null;
    const phase = dashboardRowPhase(job);
    if (phase === 'both') return null;
    if (phase === 'wash') {
      const run = formatCalendarDate(job.scheduled_date);
      return run ? `Run ${run}` : null;
    }
    const wash = formatCalendarDate(job.date_time_wash);
    return wash ? `Washed ${wash}` : null;
  };

  const todayJobs = data?.today_jobs ?? [];
  const upcomingJobs = data?.upcoming_jobs ?? [];
  const stats = data?.stats ?? { total_jobs: 0, active_jobs: 0, pending_tickets: 0 };
  const activeTimeOff = data?.active_time_off ?? null;

  if (showValues) {
    return <CoreValuesOverlay onDone={() => setShowValues(false)} />;
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {/* Greeting */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-1">
            {greeting}, {user?.firstName ?? data?.employee?.first_name}
          </h2>
          <p className="text-fieldiq-text text-sm">
            {crewRoleLabel(data?.employee, '')}
          </p>
        </div>

        {/* Days Off Banner */}
        {activeTimeOff && (
          <div className={`rounded-xl p-4 mb-6 flex items-center gap-3 ${
            activeTimeOff.is_current
              ? 'bg-blue-500/15 border border-blue-500/30'
              : 'bg-amber-500/10 border border-amber-500/20'
          }`}>
            <CalendarOff size={22} className={activeTimeOff.is_current ? 'text-blue-400' : 'text-amber-400'} />
            <div>
              <p className={`text-sm font-semibold ${activeTimeOff.is_current ? 'text-blue-400' : 'text-amber-400'}`}>
                {activeTimeOff.is_current ? 'You are currently on days off' : 'Upcoming days off'}
              </p>
              <p className="text-xs text-fieldiq-text mt-0.5">
                {parseLocalDate(activeTimeOff.start_date)}
                {' – '}
                {parseLocalDate(activeTimeOff.end_date)}
                {' · '}
                {activeTimeOff.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
              </p>
            </div>
          </div>
        )}

        {/* The clock, above the numbers. It is the one thing on this screen
            that is an action rather than a link, and the one piece of state a
            hand needs to see without looking for it. */}
        <FieldClockAction />

        {VERTICAL_ENTRIES.filter((e) => hasVertical(e.vertical)).map((e) => {
          const Icon = e.icon;
          return (
            <button
              key={e.path}
              type="button"
              onClick={() => navigate(e.path)}
              data-testid={`field-home-vertical-${e.vertical}`}
              className="mb-6 flex w-full items-center gap-3 rounded-xl border-2 border-fieldiq-border bg-fieldiq-card p-4 text-left card-hover"
            >
              <Icon size={28} className="shrink-0 text-fieldiq-accent" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-tight">{e.title}</span>
                <span className="block text-xs leading-snug text-fieldiq-text">{e.subtitle}</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-fieldiq-muted" />
            </button>
          );
        })}

        {/* Pressure testing: the hand's own PT jobs, counted by the API
            (GET /field/my-work). Every number is the server's; an absent
            summary renders '—', never 0. */}
        {hasVertical('pressure_testing') && <PtStatsStrip />}

        {/* Casing: the dashboard's crewed-job counts and job lists. Behind a
            positive guard because /employee/dashboard counts casing jobs only —
            on a tenant without casing it read "0 Active" while a PT job was in
            progress. A casing tenant renders exactly what it always did. */}
        {hasVertical('casing_running') && (<>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button onClick={() => navigate('/field/jobs', { state: { tab: 'all' } })} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
            <ClipboardList size={20} className="mx-auto mb-1 text-fieldiq-accent" />
            <div className="text-2xl font-bold text-fieldiq-accent">{stats.total_jobs}</div>
            <div className="text-xs text-fieldiq-text mt-1">Total Jobs</div>
          </button>
          <button onClick={() => navigate('/field/jobs', { state: { tab: 'active' } })} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
            <Clock size={20} className="mx-auto mb-1 text-green-400" />
            <div className="text-2xl font-bold text-green-400">{stats.active_jobs}</div>
            <div className="text-xs text-fieldiq-text mt-1">Active</div>
          </button>
          <button onClick={() => navigate('/field/tickets', { state: { tab: 'draft' } })} className="bg-fieldiq-card rounded-xl p-3 text-center card-hover">
            <Ticket size={20} className="mx-auto mb-1 text-amber-400" />
            <div className="text-2xl font-bold text-amber-400">{stats.pending_tickets}</div>
            <div className="text-xs text-fieldiq-text mt-1">Pending Tickets</div>
          </button>
        </div>

        {/* Today's Jobs */}
        <h3 className="font-semibold mb-3">Today's Jobs</h3>
        {isLoading ? (
          <div className="bg-fieldiq-card rounded-xl p-6 text-center text-fieldiq-text">Loading...</div>
        ) : todayJobs.length === 0 ? (
          <div className="bg-fieldiq-card rounded-xl p-6 text-center text-fieldiq-text">
            No jobs scheduled for today
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {todayJobs.map((job) => (
              <button
                key={job.id}
                onClick={() => navigate(`/field/jobs/${job.id}`, { state: { dayPhase: dashboardRowPhase(job) } })}
                className="w-full bg-fieldiq-card rounded-xl p-4 text-left card-hover flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-fieldiq-accent/20 flex items-center justify-center">
                    <Wrench size={20} className="text-fieldiq-accent" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{job.job_number}</h4>
                      {dashboardRowPhase(job) && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${PHASE_STYLE[dashboardRowPhase(job)!]}`}>
                          {PHASE_LABEL[dashboardRowPhase(job)!]}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-fieldiq-text">{job.customer_name}</p>
                    {job.rig && (
                      <p className="text-xs text-fieldiq-muted flex items-center gap-1 mt-0.5">
                        <MapPin size={12} /> {job.rig}
                      </p>
                    )}
                    {otherPhase(job) && (
                      <p className="text-xs text-fieldiq-muted mt-0.5">{otherPhase(job)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${jobStatusStyle(job.status)}`}>
                    {jobStatusLabel(job.status)}
                  </span>
                  <ChevronRight size={18} className="text-fieldiq-muted" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Upcoming Jobs */}
        {upcomingJobs.length > 0 && (
          <>
            <h3 className="font-semibold mb-3 mt-6">Upcoming</h3>
            <div className="space-y-3">
              {upcomingJobs.slice(0, 5).map((job) => (
                <button
                  // Upcoming lists JOB-DAYS, not jobs: a job with a wash on the
                  // 14th and a run on the 18th is TWO rows sharing one id. Keying
                  // on `job.id` alone collides -- React keeps one row and drops
                  // the other, silently, which is the wash day disappearing again
                  // by a different mechanism.
                  key={`${job.id}-${dashboardRowPhase(job) ?? 'run'}`}
                  onClick={() => navigate(`/field/jobs/${job.id}`, { state: { dayPhase: dashboardRowPhase(job) } })}
                  className="w-full bg-fieldiq-card rounded-xl p-4 text-left card-hover flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{job.job_number}</h4>
                      {dashboardRowPhase(job) && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${PHASE_STYLE[dashboardRowPhase(job)!]}`}>
                          {PHASE_LABEL[dashboardRowPhase(job)!]}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-fieldiq-text">{job.customer_name}</p>
                    {/*
                      `entry_date` is the day THIS ROW is about. It used to print
                      `scheduled_date`, which on a wash row names the RUN date --
                      the wrong day, rendered as though it were this row's own,
                      which is worse than printing nothing. The fallback covers
                      the deploy gap before API #432 is live, when the field is
                      absent and every row really is a run row.
                    */}
                    <p className="text-xs text-fieldiq-muted mt-0.5">
                      {formatCalendarDate(job.entry_date ?? job.scheduled_date) ?? '—'}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-fieldiq-muted" />
                </button>
              ))}
            </div>
          </>
        )}
        </>)}

        {/* Shortcuts -- one tile per More-tab destination, plus My Jobs */}
        {/* The heading and the tile labels below carry no colour of their own,
            like every other heading on this screen. index.css sets body's colour
            from --fieldiq-heading, and that token is redefined per theme, so an
            uncoloured label already resolves to the right value in both. The
            colour utility that used to sit here did nothing: removing it live
            from these elements left the computed colour unchanged at #1C2430 on
            the light ground. Do not add it back "for light mode". */}
        <h3 className="font-semibold mb-3 mt-6">Shortcuts</h3>
        <FieldTileGrid tiles={SHORTCUTS.filter((tile) => !tile.vertical || hasVertical(tile.vertical))} />

        {/* Below the tiles, not among them. The shared component, not a copy:
            it is wired to the one AuthContext logout, which purges every
            tenant-scoped cache. A forked handler here would sign the user out
            of the session and leave the previous tenant's data in memory. */}
        <SignOutButton />
      </div>
    </div>
  );
}
